package songwritersnotebook.app.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.QueueMusic
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import songwritersnotebook.app.data.GuitarTab
import songwritersnotebook.app.data.RecordingMetadata
import songwritersnotebook.app.database.UploadStatus
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LineMediaSheet(
    recordings: List<RecordingMetadata>,
    tabs: List<GuitarTab>,
    activeRecordingId: String?,
    onDismiss: () -> Unit,
    onPlayRecording: (RecordingMetadata) -> Unit,
    onDeleteRecording: (RecordingMetadata) -> Unit,
    onRecordNew: () -> Unit,
    onRenameRecording: (RecordingMetadata, String) -> Unit,
    onReRecord: (RecordingMetadata) -> Unit,
    onEditTab: (GuitarTab) -> Unit,
    onDeleteTab: (GuitarTab) -> Unit,
    onNewTab: () -> Unit,
    canUseTabs: Boolean,
    onUpgrade: () -> Unit,
    initialTabIndex: Int? = null,
    uploadStatus: Map<String, UploadStatus> = emptyMap(),
    onRetryUpload: (RecordingMetadata) -> Unit = {},
    onMoveRecording: ((RecordingMetadata) -> Unit)? = null,
    saveStatus: songwritersnotebook.app.viewmodels.SaveStatus = songwritersnotebook.app.viewmodels.SaveStatus.IDLE,
    onSaveRequested: () -> Unit = {},
    isCloudSyncEnabled: Boolean = true
) {
    var selectedTabIndex by remember {
        mutableStateOf(initialTabIndex ?: if (tabs.isNotEmpty() && recordings.isEmpty()) 1 else 0)
    }
    val tabTitles = listOf("Recordings", "Guitar Tabs")

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                //.navigationBarsPadding() // KMP: May need to be handled by scaffold or manual padding if not available
                .padding(bottom = 16.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.padding(horizontal = 16.dp)
            ) {
                Text(
                    "Line Media",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.weight(1f)
                )
                SaveStatusIndicator(
                    saveStatus = saveStatus,
                    isCloudSyncEnabled = isCloudSyncEnabled,
                    onClick = onSaveRequested
                )
            }
            Spacer(modifier = Modifier.height(16.dp))

            TabRow(selectedTabIndex = selectedTabIndex) {
                tabTitles.forEachIndexed { index, title ->
                    Tab(
                        selected = selectedTabIndex == index,
                        onClick = { selectedTabIndex = index },
                        text = { Text(title) },
                        icon = {
                            Icon(
                                if (index == 0) Icons.Default.Mic else Icons.AutoMirrored.Filled.QueueMusic,
                                contentDescription = null
                            )
                        }
                    )
                }
            }

            Box(modifier = Modifier.weight(1f, fill = false)) {
                when (selectedTabIndex) {
                    0 -> RecordingsList(
                        recordings = recordings,
                        activeRecordingId = activeRecordingId,
                        onPlay = onPlayRecording,
                        onDelete = onDeleteRecording,
                        onRename = onRenameRecording,
                        onNew = onRecordNew,
                        onReRecord = onReRecord,
                        uploadStatus = uploadStatus,
                        onRetryUpload = onRetryUpload,
                        onMoveRecording = onMoveRecording
                    )
                    1 -> TabsList(
                        tabs = tabs,
                        onEdit = onEditTab,
                        onDelete = onDeleteTab,
                        onNew = onNewTab,
                        canUseTabs = canUseTabs,
                        onUpgrade = onUpgrade
                    )
                }
            }
        }
    }
}

@Composable
fun RecordingsList(
    recordings: List<RecordingMetadata>,
    activeRecordingId: String?,
    onPlay: (RecordingMetadata) -> Unit,
    onDelete: (RecordingMetadata) -> Unit,
    onRename: (RecordingMetadata, String) -> Unit,
    onNew: () -> Unit,
    onReRecord: (RecordingMetadata) -> Unit,
    uploadStatus: Map<String, UploadStatus>,
    onRetryUpload: (RecordingMetadata) -> Unit,
    onMoveRecording: ((RecordingMetadata) -> Unit)?
) {
    var selectedRecording by remember { mutableStateOf<RecordingMetadata?>(null) }

    LaunchedEffect(activeRecordingId) {
        if (activeRecordingId != null) {
            selectedRecording = recordings.find { it.id == activeRecordingId }
        }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            // Background click to deselect
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null
            ) { selectedRecording = null }
    ) {
        LazyColumn(
            modifier = Modifier.weight(1f),
            contentPadding = PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            if (recordings.isEmpty()) {
                item { EmptyStateMessage("No recordings yet.") }
            }
            items(recordings) { recording ->
                LineMediaRecordingItem(
                    recording = recording,
                    isPlaying = recording.id == activeRecordingId,
                    isSelected = selectedRecording?.id == recording.id,
                    uploadStatus = uploadStatus[recording.id],
                    onRetryUpload = { onRetryUpload(recording) },
                    onPlayToggle = {
                        onPlay(recording)
                        selectedRecording = recording
                    },
                    // Toggle selection on tap
                    onSelect = {
                        if (selectedRecording?.id == recording.id) selectedRecording = null
                        else selectedRecording = recording
                    },
                    onDelete = { onDelete(recording); if (selectedRecording?.id == recording.id) selectedRecording = null },
                    onRename = { newName: String -> onRename(recording, newName) },
                    onMove = if (onMoveRecording != null) { { onMoveRecording(recording) } } else null
                )
            }
        }

        if (selectedRecording != null) {
            ConfigButton(
                text = "Re-record '${selectedRecording?.label}'",
                icon = Icons.Default.Mic,
                onClick = { onReRecord(selectedRecording!!) },
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.errorContainer, contentColor = MaterialTheme.colorScheme.onErrorContainer)
            )
        } else {
            ConfigButton(text = "Record New Audio", icon = Icons.Default.Mic, onClick = onNew)
        }
    }
}

@Composable
fun TabsList(
    tabs: List<GuitarTab>,
    onEdit: (GuitarTab) -> Unit,
    onDelete: (GuitarTab) -> Unit,
    onNew: () -> Unit,
    canUseTabs: Boolean,
    onUpgrade: () -> Unit
) {
    Box(modifier = Modifier.fillMaxSize()) {
        // Content (Blurred or dimmed if restricted)
        Column(modifier = Modifier.fillMaxSize()) {
            LazyColumn(
                modifier = Modifier.weight(1f),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (tabs.isEmpty()) {
                    item { EmptyStateMessage("No tabs created yet.") }
                }
                items(tabs) { tab ->
                    LineMediaTabItem(tab, onEdit, onDelete)
                }
            }
            ConfigButton(text = "Create New Tab", icon = Icons.Default.Add, onClick = onNew)
        }

        // Overlay for Restricted Members
        if (!canUseTabs) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.85f))
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { },
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                    modifier = Modifier.padding(24.dp)
                ) {
                    Icon(
                        Icons.Default.Lock,
                        contentDescription = "Locked",
                        modifier = Modifier.size(48.dp),
                        tint = MaterialTheme.colorScheme.primary
                    )
                    Text(
                        "Guitar tabs are a premium feature.",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center
                    )
                    Text(
                        "Upgrade your plan to create and edit guitar tabs.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center
                    )
                    Button(onClick = onUpgrade) {
                        Text("Upgrade Now")
                    }
                }
            }
        }
    }
}

@Composable
fun ConfigButton(
    text: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    onClick: () -> Unit,
    colors: ButtonColors = ButtonDefaults.buttonColors()
) {
    Button(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth().padding(16.dp),
        colors = colors
    ) {
        Icon(icon, null, modifier = Modifier.size(18.dp))
        Spacer(Modifier.width(8.dp))
        Text(text)
    }
}

@Composable
fun LineMediaRecordingItem(
    recording: RecordingMetadata,
    isPlaying: Boolean,
    isSelected: Boolean,
    uploadStatus: songwritersnotebook.app.database.UploadStatus? = null,
    onRetryUpload: () -> Unit = {},
    onPlayToggle: () -> Unit,
    onSelect: () -> Unit,
    onDelete: () -> Unit,
    onRename: (String) -> Unit,
    onMove: (() -> Unit)? = null
) {
    var isEditing by remember { mutableStateOf(false) }
    var editName by remember { mutableStateOf(recording.label) }
    
    // KMP Date Formatting
    val dateText = remember(recording.timestamp) {
        val instant = Instant.fromEpochMilliseconds(recording.timestamp)
        val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
        // Simplified format: 2023-10-25 14:30
        "${localDateTime.date} ${localDateTime.hour.toString().padStart(2, '0')}:${localDateTime.minute.toString().padStart(2, '0')}"
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(if (isSelected) MaterialTheme.colorScheme.surfaceVariant else MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
            .border(if (isSelected) 1.dp else 0.dp, if (isSelected) MaterialTheme.colorScheme.primary else androidx.compose.ui.graphics.Color.Transparent, RoundedCornerShape(8.dp))
            .clickable { onSelect() } // Toggle logic handled by parent
            .padding(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (uploadStatus == songwritersnotebook.app.database.UploadStatus.UPLOADING) {
            CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
        } else {
            IconButton(onClick = onPlayToggle) {
                Icon(
                    imageVector = if (isPlaying) Icons.Default.Stop else Icons.Default.PlayArrow,
                    contentDescription = if (isPlaying) "Stop" else "Play",
                    tint = MaterialTheme.colorScheme.primary
                )
            }
        }

        Spacer(modifier = Modifier.width(8.dp))

        Column(modifier = Modifier.weight(1f)) {
            if (isEditing) {
                androidx.compose.foundation.text.BasicTextField(
                    value = editName,
                    onValueChange = { editName = it },
                    textStyle = MaterialTheme.typography.bodyLarge.copy(color = MaterialTheme.colorScheme.onSurface),
                    singleLine = true,
                    modifier = Modifier
                        .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(4.dp))
                        .padding(4.dp)
                )
            } else {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = recording.label,
                        style = MaterialTheme.typography.bodyLarge,
                        fontWeight = if (isPlaying) FontWeight.Bold else FontWeight.Normal,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f, fill = false)
                    )
                    
                    val recordingSaveStatus = when {
                        uploadStatus == songwritersnotebook.app.database.UploadStatus.UPLOADING || uploadStatus == songwritersnotebook.app.database.UploadStatus.PENDING -> songwritersnotebook.app.viewmodels.SaveStatus.SAVING
                        uploadStatus == songwritersnotebook.app.database.UploadStatus.SUCCESS || recording.isSynced -> songwritersnotebook.app.viewmodels.SaveStatus.SAVED
                        uploadStatus == songwritersnotebook.app.database.UploadStatus.ERROR -> songwritersnotebook.app.viewmodels.SaveStatus.ERROR
                        else -> songwritersnotebook.app.viewmodels.SaveStatus.IDLE
                    }

                    SaveStatusIndicator(
                        saveStatus = recordingSaveStatus,
                        onClick = { if (uploadStatus == songwritersnotebook.app.database.UploadStatus.ERROR) onRetryUpload() },
                        showText = isSelected // Show text like "Saved to cloud" only when item is selected to avoid clutter
                    )
                }
            }
            Text(
                text = dateText,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        Row {
            if (isEditing) {
                IconButton(onClick = { onRename(editName); isEditing = false }) {
                    Icon(Icons.Default.Check, "Save", tint = MaterialTheme.colorScheme.primary)
                }
            } else {
                IconButton(onClick = { isEditing = true; onSelect() }) { // Select on edit
                    Icon(Icons.Default.Edit, "Rename", modifier = Modifier.size(20.dp))
                }
            }
            
            // Move Button
            if (onMove != null) {
                IconButton(onClick = onMove) {
                    Icon(Icons.AutoMirrored.Filled.ArrowForward, "Move", modifier = Modifier.size(20.dp))
                }
            }
            
            IconButton(onClick = onDelete) {
                Icon(Icons.Default.Delete, "Delete", tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(20.dp))
            }
        }
    }
}

@Composable
fun EmptyStateMessage(msg: String) {
    Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
        Text(msg, color = MaterialTheme.colorScheme.onSurfaceVariant)
    }
}

@Composable
fun LineMediaTabItem(
    tab: GuitarTab,
    onEdit: (GuitarTab) -> Unit,
    onDelete: (GuitarTab) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
            .clickable { onEdit(tab) }
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = tab.name.ifBlank { "Untitled Tab" },
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = "${tab.beats} beats, ${tab.columns.size / tab.subdivisions} bars",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        Row {
            IconButton(onClick = { onEdit(tab) }) {
                Icon(Icons.Default.Edit, "Edit", modifier = Modifier.size(20.dp))
            }
            IconButton(onClick = { onDelete(tab) }) {
                Icon(Icons.Default.Delete, "Delete", tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(20.dp))
            }
        }
    }
}
